import type { IconName } from "@repo/fika-design-system/icon"
import { createPolled } from "@solid-primitives/timer"
import { createQuery } from "@urql/solid"
import { formatDistanceToNow } from "date-fns"
import { createMemo, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Text } from "@/designSystem/components/text"
import { Well } from "@/designSystem/components/well"
import { Section } from "@/designSystem/patterns/section"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { statusBadgeClass } from "./_publication-export-section.css"
import { PublicationTable } from "./_publication-table"

interface PublicationExportSectionProps {
  title: string
  iconName: IconName
  id: string
}
export function PublicationExportSection(props: PublicationExportSectionProps) {
  const { LL } = useI18nContext()

  const [exportsResult, refetchExports] = createQuery({
    query: graphql(`
      query PublicationExports {
        publicationExports {
          id
          numPosts
          numSubscribers
          completedAt
          error
          createdAt
        }
      }
    `),
    requestPolicy: "cache-and-network",
  })

  const [createExportState, createExport] = createMutationWithErrorHandling(
    graphql(
      `
        mutation CreatePublicationExport {
          createPublicationExport {
            ... on MutationCreatePublicationExportSuccess {
              data {
                id
              }
            }
            ...ErrorFragment
          }
        }
      `,
      [ErrorFragment],
    ),
    "createPublicationExport",
  )

  const exports = createMemo(() => exportsResult.data?.publicationExports ?? [])

  const hasProcessingExport = createMemo(() =>
    exports().some(e => !e.completedAt && !e.error),
  )

  createPolled(
    () => {
      if (hasProcessingExport()) {
        refetchExports({ requestPolicy: "network-only" })
      }
      return null
    },
    () => (hasProcessingExport() ? 3000 : false),
  )

  async function handleGenerateExport() {
    const result = await createExport({})

    if (result.success) {
      refetchExports({ requestPolicy: "network-only" })
    }
  }

  function getExportStatus(exportItem: {
    completedAt: Date | null
    error: string | null
  }) {
    if (exportItem.error) return "error"
    if (exportItem.completedAt) return "completed"
    return "processing"
  }

  function downloadExport(exportId: string) {
    window.open(`/api/exports/${exportId}/download`, "_blank")
  }

  return (
    <Section
      title={props.title}
      iconName={props.iconName}
      id={props.id}
      description={LL().blog.export.description()}
      hue="amber"
    >
      <Well>
        <Box gap="16">
          <Show
            when={exports().length > 0}
            fallback={
              <Text color="sand700" fontSize="s">
                {LL().blog.export.noExports()}
              </Text>
            }
          >
            <PublicationTable>
              <PublicationTable.Head>
                <PublicationTable.Header>
                  {LL().blog.export.exportedAt()}
                </PublicationTable.Header>
                <PublicationTable.Header>
                  {LL().blog.export.subscribers()}
                </PublicationTable.Header>
                <PublicationTable.Header>
                  {LL().blog.export.posts()}
                </PublicationTable.Header>
                <PublicationTable.Header />
              </PublicationTable.Head>
              <PublicationTable.Body>
                <For each={exports()}>
                  {exportItem => {
                    const status = createMemo(() => getExportStatus(exportItem))

                    return (
                      <PublicationTable.Row>
                        <PublicationTable.Cell>
                          <Text fontSize="s" color="sand800">
                            {formatDistanceToNow(
                              new Date(exportItem.createdAt),
                              {
                                addSuffix: true,
                              },
                            )}
                          </Text>
                        </PublicationTable.Cell>
                        <PublicationTable.Cell>
                          <Text fontSize="s" color="sand800">
                            {exportItem.numSubscribers ?? "-"}
                          </Text>
                        </PublicationTable.Cell>
                        <PublicationTable.Cell>
                          <Text fontSize="s" color="sand800">
                            {exportItem.numPosts ?? "-"}
                          </Text>
                        </PublicationTable.Cell>
                        <PublicationTable.Cell>
                          <Show
                            when={status() === "completed"}
                            fallback={
                              <span
                                class={statusBadgeClass({
                                  status: status(),
                                })}
                              >
                                {status() === "processing"
                                  ? LL().blog.export.processing()
                                  : LL().blog.export.error()}
                              </span>
                            }
                          >
                            <Button
                              hue="amber"
                              kind="surface"
                              size="s"
                              onClick={() => downloadExport(exportItem.id)}
                            >
                              {LL().blog.export.download()}
                            </Button>
                          </Show>
                        </PublicationTable.Cell>
                      </PublicationTable.Row>
                    )
                  }}
                </For>
              </PublicationTable.Body>
            </PublicationTable>
          </Show>

          <Box justifyContent="flex-end" flexDirection="row">
            <Button
              hue="amber"
              onClick={handleGenerateExport}
              disabled={createExportState.fetching || hasProcessingExport()}
              active={createExportState.fetching}
            >
              {createExportState.fetching
                ? LL().blog.export.generating()
                : LL().blog.export.generateExport()}
            </Button>
          </Box>
        </Box>
      </Well>
    </Section>
  )
}
